import React, { useEffect, useState } from 'react';
import { useQuery, useNavigate } from '@common/hooks';
import { Button, Empty } from 'antd';
import {
  SHIFTS_BY_DAY,
  SHIFT_GAPS_BY_DAY,
  LOCALSTORAGE_SHIFT_CALENDAR_FILTER_KEY,
} from '@scheduling/graphql';
import {
  Drawer,
  LoadingState,
  Tabs,
  InfiniteScroll,
  ErrorHandler,
  Icon,
  Link,
} from '@common/components';
import moment from 'moment';
import { ShiftsCalendarDetailsCard } from '@scheduling/components';
import { debounce } from 'lodash';
import { sanitizeLength } from '@scheduling/services';
import Filters from 'works/legacy/lib/filters';
import {
  ShiftGapsByDayQueryCgResponse,
  ShiftsByDayQueryCgResponse,
} from '@common/graphql/schema';
import { buildDialogUrl } from '@common/services';
import { useCurrentMembership } from '@identity/hooks';

const QUERY_LIMIT = 10;

const STATES = {
  open: ['open'],
  confirmed: ['confirmed'],
  archived: ['cancelled', 'archived'],
  claimed: ['claimed'],
};

export function ShiftsCalendarDetailsDrawer({
  open,
  onClose,
  date,
}: {
  open: boolean;
  onClose: () => void;
  date?: string;
}) {
  const navigate = useNavigate();
  const [currentTab, setCurrentTab] = useState('open');
  const [loadQueries, setLoadQueries] = useState(false);

  const isPastDate = moment(date).isBefore(moment().startOf('day'));

  useEffect(() => {
    // Waits drawer finish opening to prevent performance issue
    setTimeout(() => setLoadQueries(true), 300);
  }, []);

  useEffect(() => {
    setCurrentTab(isPastDate ? 'confirmed' : 'open');
  }, [isPastDate]);

  const localStorageFilters = Filters.getFilters(
    LOCALSTORAGE_SHIFT_CALENDAR_FILTER_KEY
  );

  const filters = {
    ...localStorageFilters,
    length: sanitizeLength(localStorageFilters['length']),
  };

  const loadMore = debounce(() => {
    const { fetchMore, totalCount, currentCount } =
      currentTab === 'confirmed'
        ? {
            fetchMore: fetchMoreConfirmed,
            totalCount: confirmedOpeningsCount,
            currentCount: confirmedOpenings.length,
          }
        : currentTab === 'pending_approval'
          ? {
              fetchMore: fetchMorePendingApproval,
              totalCount: pendingApprovalOpeningsCount,
              currentCount: pendingApprovalOpenings.length,
            }
          : {
              fetchMore: fetchMoreArchived,
              totalCount: archivedOpeningsCount,
              currentCount: archivedOpenings.length,
            };

    if (totalCount === currentCount) {
      return;
    }

    fetchMore({
      variables: {
        offset: currentCount,
        limit: QUERY_LIMIT,
        filters: filters,
      },
      updateQuery: (
        prev: {
          shiftsByDay: ShiftsByDayQueryCgResponse['shiftsByDay'];
        },
        { fetchMoreResult }
      ) => {
        if (!fetchMoreResult) return prev;
        return Object.assign(
          {} as {
            shiftsByDay: ShiftsByDayQueryCgResponse['shiftsByDay'];
          },
          prev,
          {
            shiftsByDay: {
              ...prev?.shiftsByDay,
              shiftGapOpenings: [
                ...prev.shiftsByDay.shiftGapOpenings,
                ...fetchMoreResult.shiftsByDay.shiftGapOpenings,
              ],
            },
          }
        );
      },
    });
  }, 500);

  const loadMoreOpen = debounce(() => {
    if (openShiftGaps.length >= openShiftGapsCount) {
      return;
    }

    fetchMoreOpen({
      variables: {
        offset: openShiftGaps.length,
        limit: QUERY_LIMIT,
        filters: filters,
      },
      updateQuery: (
        prev: {
          shiftGapsByDay: ShiftGapsByDayQueryCgResponse['shiftGapsByDay'];
        },
        { fetchMoreResult }
      ) => {
        if (!fetchMoreResult) return prev;

        const combinedShiftGaps = [
          ...prev.shiftGapsByDay.shiftGaps,
          ...fetchMoreResult.shiftGapsByDay.shiftGaps,
        ];
        const uniqueShiftGaps = Array.from(
          combinedShiftGaps
            .reduce((map, item) => map.set(item.id, item), new Map())
            .values()
        );

        return Object.assign({}, prev, {
          shiftGapsByDay: {
            ...prev.shiftGapsByDay,
            shiftGaps: uniqueShiftGaps,
          },
        });
      },
    });
  }, 500);

  const currentMembership = useCurrentMembership();

  const {
    data: openShiftGapsData,
    fetchMore: fetchMoreOpen,
    loading: openShiftGapsLoading,
    error: openShiftGapsError,
  } = useQuery(SHIFT_GAPS_BY_DAY, {
    variables: {
      date: date!,
      offset: 0,
      limit: QUERY_LIMIT,
      filters: filters,
      states: STATES['open'],
    },
    skip: !date || !loadQueries,
  });

  const {
    data: pendingApprovalOpeningsData,
    fetchMore: fetchMorePendingApproval,
    loading: pendingApprovalOpeningsLoading,
    error: pendingApprovalOpeningsError,
  } = useQuery(SHIFTS_BY_DAY, {
    variables: {
      date: date!,
      offset: 0,
      limit: QUERY_LIMIT,
      filters: filters,
      states: STATES['claimed'],
    },
    skip: !date || !loadQueries,
  });

  const {
    data: confirmedOpeningsData,
    fetchMore: fetchMoreConfirmed,
    loading: confirmedOpeningsLoading,
    error: confirmedOpeningsError,
  } = useQuery(SHIFTS_BY_DAY, {
    variables: {
      date: date!,
      offset: 0,
      limit: QUERY_LIMIT,
      filters: filters,
      states: STATES['confirmed'],
    },
    skip: !date || !loadQueries,
  });

  const {
    data: archivedOpeningsData,
    fetchMore: fetchMoreArchived,
    loading: archivedOpeningsLoading,
    error: archivedOpeningsError,
  } = useQuery(SHIFTS_BY_DAY, {
    variables: {
      date: date!,
      offset: 0,
      limit: QUERY_LIMIT,
      filters: filters,
      states: STATES['archived'],
    },
    skip: !date || !loadQueries,
  });

  const error =
    openShiftGapsError ||
    pendingApprovalOpeningsError ||
    confirmedOpeningsError ||
    archivedOpeningsError;

  if (error) {
    return (
      <ErrorHandler
        error={error}
        log="ShiftsCalendarDetailsDrawer"
        messageType="contextual"
      />
    );
  }

  const loading =
    openShiftGapsLoading ||
    pendingApprovalOpeningsLoading ||
    confirmedOpeningsLoading ||
    archivedOpeningsLoading;

  const canCreateShift = currentMembership.canCreateShiftGaps;
  const canSendShift = currentMembership.canShowShiftTargeting;

  const data =
    openShiftGapsData || confirmedOpeningsData || archivedOpeningsData;

  const buildShiftDetailsDrawerUrl = (id: string) =>
    buildDialogUrl('shifts-request-details-drawer', {
      overlap: true,
      props: {
        shiftId: id,
        type: 'calendar',
      },
    });

  const onTabChange = (activeKey: string) => {
    setCurrentTab(activeKey);
  };

  const onCardClick = (id: string) => {
    setCurrentTab('open');
    navigate(buildShiftDetailsDrawerUrl(id));
  };

  const openShiftGaps = openShiftGapsData?.shiftGapsByDay.shiftGaps || [];
  const pendingApprovalOpenings =
    pendingApprovalOpeningsData?.shiftsByDay.shiftGapOpenings || [];
  const confirmedOpenings =
    confirmedOpeningsData?.shiftsByDay.shiftGapOpenings || [];
  const archivedOpenings =
    archivedOpeningsData?.shiftsByDay.shiftGapOpenings || [];

  const openOpeningsCount =
    openShiftGapsData?.shiftGapsByDay.totalOpenOpenings || 0;
  const openShiftGapsCount = openShiftGapsData?.shiftGapsByDay.totalCount || 0;
  const pendingApprovalOpeningsCount =
    pendingApprovalOpeningsData?.shiftsByDay.totalCount || 0;
  const confirmedOpeningsCount =
    confirmedOpeningsData?.shiftsByDay.totalCount || 0;
  const archivedOpeningsCount =
    archivedOpeningsData?.shiftsByDay.totalCount || 0;

  function buildSelfDrawerRoute(date: string) {
    return buildDialogUrl('shifts-calendar-details-drawer', {
      overlap: false,
      props: { date },
    });
  }

  function buildCreateShiftDrawerRoute() {
    return buildDialogUrl('create-shift-drawer', {
      overlap: true,
      props: { startDate: date || '' },
    });
  }

  function buildSendShiftDrawerRoute(shiftId: string, type: string) {
    return buildDialogUrl('send-shift-drawer', {
      overlap: true,
      props: {
        shiftId,
        type,
      },
    });
  }

  return (
    <>
      <Drawer
        open={open}
        className="ShiftsCalendarDetailsDrawer"
        title={
          <>
            <Button
              type="link"
              style={{ paddingLeft: 0, paddingRight: 0 }}
              onClick={() => {
                const newDate = moment(date)
                  .subtract(1, 'day')
                  .format('YYYY-MM-DD');
                navigate(buildSelfDrawerRoute(newDate));
              }}
            >
              <Icon icon="chevronLeft" />
            </Button>
            <Button
              style={{ paddingLeft: 24, paddingRight: 16 }}
              type="link"
              onClick={() => {
                const newDate = moment(date).add(1, 'day').format('YYYY-MM-DD');
                navigate(buildSelfDrawerRoute(newDate));
              }}
            >
              <Icon icon="chevronRight" />
            </Button>
            {moment(date).format('dddd, MMMM D YYYY')}
          </>
        }
        onClose={onClose}
        footer={
          canCreateShift &&
          !isPastDate && (
            <Link to={buildCreateShiftDrawerRoute()}>
              <Button size="large">Create Shift</Button>
            </Link>
          )
        }
      >
        {loading || !date || !loadQueries ? (
          <LoadingState />
        ) : (
          data && (
            <Tabs onChange={onTabChange} activeKey={currentTab}>
              {!isPastDate && (
                <Tabs.Pane key="open" tab={`Open (${openOpeningsCount})`}>
                  <div className="shift-cards">
                    {!openOpeningsCount ? (
                      <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                    ) : (
                      <InfiniteScroll
                        dataLength={openShiftGaps.length}
                        hasMore={openShiftGaps.length < openOpeningsCount}
                        next={loadMoreOpen}
                        elementClassName=".ShiftsCalendarDetailsDrawer .ant-drawer-body"
                      >
                        {openShiftGaps.map((shiftGap) => (
                          <div key={shiftGap.id}>
                            <ShiftsCalendarDetailsCard
                              key={shiftGap.id}
                              shiftGap={shiftGap}
                              onClick={onCardClick}
                              openingCount={
                                shiftGap?.openShiftGapOpenings?.length
                              }
                              canSendShift={canSendShift}
                              onPressSendShift={() => {
                                navigate(
                                  buildSendShiftDrawerRoute(shiftGap.id, 'list')
                                );
                              }}
                            />
                          </div>
                        ))}
                      </InfiniteScroll>
                    )}
                  </div>
                </Tabs.Pane>
              )}
              {pendingApprovalOpeningsCount > 0 && (
                <Tabs.Pane
                  key="pending_approval"
                  tab={`Pending Approval (${pendingApprovalOpeningsCount})`}
                >
                  <div className="shift-cards">
                    <InfiniteScroll
                      dataLength={confirmedOpenings.length}
                      next={loadMore}
                      hasMore={
                        pendingApprovalOpenings.length <
                        pendingApprovalOpeningsCount
                      }
                      elementClassName=".ShiftsCalendarDetailsDrawer .ant-drawer-body"
                    >
                      {pendingApprovalOpenings.map((opening) => (
                        <ShiftsCalendarDetailsCard
                          key={opening.id}
                          shiftOpening={opening}
                          onClick={onCardClick}
                        />
                      ))}
                    </InfiniteScroll>
                  </div>
                </Tabs.Pane>
              )}
              <Tabs.Pane
                key="confirmed"
                tab={`Confirmed (${confirmedOpeningsCount})`}
              >
                <div className="shift-cards">
                  {!confirmedOpeningsCount ? (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                  ) : (
                    <InfiniteScroll
                      dataLength={confirmedOpenings.length}
                      hasMore={
                        confirmedOpenings.length < confirmedOpeningsCount
                      }
                      next={loadMore}
                      elementClassName=".ShiftsCalendarDetailsDrawer .ant-drawer-body"
                    >
                      {confirmedOpenings.map((opening) => (
                        <ShiftsCalendarDetailsCard
                          key={opening.id}
                          shiftOpening={opening}
                          onClick={onCardClick}
                        />
                      ))}
                    </InfiniteScroll>
                  )}
                </div>
              </Tabs.Pane>
              <Tabs.Pane
                key="archived"
                tab={`Archived (${archivedOpeningsCount})`}
              >
                <div className="shift-cards">
                  {!archivedOpeningsCount && (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                  )}
                  <InfiniteScroll
                    dataLength={archivedOpenings.length}
                    hasMore={archivedOpenings.length < archivedOpeningsCount}
                    next={loadMore}
                    elementClassName=".ShiftsCalendarDetailsDrawer .ant-drawer-body"
                  >
                    {archivedOpenings.map((opening) => (
                      <ShiftsCalendarDetailsCard
                        key={opening.id}
                        shiftOpening={opening}
                        showStatePill
                        onClick={onCardClick}
                      />
                    ))}
                  </InfiniteScroll>
                </div>
              </Tabs.Pane>
            </Tabs>
          )
        )}
      </Drawer>
      <style jsx>{`
        :global(.ShiftsCalendarDetailsDrawer .ant-drawer-body) {
          padding-top: 0;
        }
        :global(.ShiftsCalendarDetailsDrawer .shift-cards) {
          margin-top: 16px;
        }
      `}</style>
    </>
  );
}
